// src/components/AttendanceTab.jsx
import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { MEETING_ACTIVE } from '../../core/meeting-statuses.mjs'
import { useAttendance } from '../hooks/useAttendance.js'
import { useOverrides } from '../hooks/useOverrides.js'
import { useViewPreference } from '../hooks/useViewPreference.js'
import { useDeliberateBackdropDismiss } from '../hooks/useDeliberateBackdropDismiss.js'
import { useDialog } from '../hooks/useDialog.js'
import {
  toDateStr, dayKey, dateRange, weekStart, monthBounds,
  scheduledMinutes, scheduledLocation, attendanceStatusFor, actualMinutes,
  diffMinutes, fmtDuration, fmtDelta, summarise, deriveStatus,
} from '../lib/attendance.js'
import { daySlots, DAYS, scheduledSlotsOnDate } from '../lib/schedule.js'
import { fmtPhone } from '../lib/phone.js'
// Office-timezone + server-corrected clock: attendance "today"/"now" and stamped check-in times
// must be Tbilisi time from a trusted clock, never the device's tz/clock. See lib/clock.js.
import { now, todayStr, tzTimeStr, tzHour, tzMinute, useClock } from '../lib/clock.js'
import { t, getLanguage } from '../lib/i18n.js'
import { useCandidates } from '../hooks/useCandidates.js'
import { logCandidateEvent } from '../lib/agentLog.js'
import { TimeInput } from './TimeInput.jsx'
import { Icon } from './Icon.jsx'
import { COUNTER, pill } from './desk/primitives.jsx'
import { getMeetingMarkers } from '../db/core.js'
import { tbilisiDayWindow } from '../lib/tbilisiTime.js'
import { keepInView } from '../lib/keepInView.js'

const dateLocale = (lang) => (lang || getLanguage()) === 'ka' ? 'ka-GE' : 'en-GB'

// ⚠️ `icon` is an Icon.jsx NAME, not an emoji (2026-08-10). Every consumer renders it through
// <Icon>, so it inherits `currentColor` and picks up the `color` class on the same row — which
// an emoji cannot do, and colour is most of what this tab communicates.
const STATUS_META = {
  present: { get label() { return t('att_in_office') },   icon: 'building', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  remote:  { get label() { return t('att_remote_short') }, icon: 'house', color: 'text-blue-500',                          bg: 'bg-blue-100/70 dark:bg-blue-900/20'   },
  absent:  { get label() { return t('att_absent') },       icon: 'x',  color: 'text-red-500',                           bg: 'bg-red-100 dark:bg-red-900/20'        },
  holiday: { get label() { return t('att_status_holiday') }, icon: 'tree-palm', color: 'text-purple-500',                        bg: 'bg-purple-100 dark:bg-purple-900/20'  },
}

// ─── Attendance entry modal ────────────────────────────────────────────────────
export function EntryModal({ agent, date, record, onSave, onDelete, onClose, lang, scheduleHistory, overrideMap = {} }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  // a REAL dialog (attendance audit 2026-10-21): role, name, focus moved in and kept in, Escape closes
  const { titleId, dialogProps } = useDialog(onClose)
  const dk        = dayKey(date)
  const schedSlots = scheduledSlotsOnDate(agent, date, scheduleHistory, overrideMap)
  const scheduled  = scheduledMinutes(agent, date, scheduleHistory, overrideMap)

  // Tbilisi wall-clock 'HH:MM' from the SERVER-corrected instant — a wrong PC clock or a device
  // in another timezone no longer stamps a wrong check-in time (2026-07-21).
  function nowStr() { return tzTimeStr(now()) }

  // Build initial per-slot state from existing record — NO pre-fill from schedule
  //
  // 🔴 A SELF-CHECK-IN HAS NO SLOTS, AND THIS MODAL USED TO BE BLIND TO IT (owner, 2026-09-10:
  // "agent has times in day view and not when I switch to weekly and open its card"). The agent's
  // own arrival is written by `mark_own_arrival` (scheduler migration 0002), which sets the
  // TOP-LEVEL `attendance.check_in` and never touches `slots` — likewise departure. Every
  // other reader already knows this: the day card reads
  // `rec?.check_in || rec?.slots?.find(s => s.check_in)?.check_in`, and `actualMinutes` falls
  // back to the top-level pair whenever `slots` is empty. Only this editor asked `slots` alone,
  // so a real 11:06 arrival rendered as two blank boxes.
  //
  // ⚠️ AND THE BLANKS WERE NOT MERELY COSMETIC — SAVING THEM DESTROYED THE ARRIVAL. handleSave
  // derives the top-level columns FROM these rows, so opening the card and pressing save (or
  // marking the day შვებულება, which is the only place that verb lives) wrote check_in: null and
  // a slots array of nulls. That is worse than the blank display: `actualMinutes` prefers slots
  // whenever they are non-empty, so the day the agent really worked then counted as ZERO minutes
  // — in the stats, in the deltas, in payroll — and the original time was gone from the row.
  //
  // ⚠️ First slot takes the arrival, LAST slot takes the departure. That is not symmetry for its
  // own sake: it is the convention the quick check-in/check-out buttons below already write
  // (`i === 0` for in, `lastIdx` for out), and on a split day the departure belongs to the
  // evening slot, not the morning one. On a single-slot day — which is what a self-check-in
  // almost always is — they are the same row.
  function initSlotRows() {
    if (schedSlots.length === 0) return []
    const stored     = record?.slots || []
    const slotHasIn  = stored.some(s => s.check_in)
    const slotHasOut = stored.some(s => s.check_out)
    const lastIdx    = schedSlots.length - 1
    return schedSlots.map((sched, i) => {
      const existing = record?.slots?.find(s => s.slot_index === i)
      // Adopt the top-level time ONLY when no slot carries one — a slot that has been edited
      // by hand is the later statement and must never be overwritten by the legacy column.
      const adoptIn  = !slotHasIn  && i === 0       ? (record?.check_in  ?? '') : ''
      const adoptOut = !slotHasOut && i === lastIdx ? (record?.check_out ?? '') : ''
      return {
        slot_index: i,
        check_in:  existing?.check_in  ?? adoptIn  ?? '',   // empty unless already recorded
        check_out: existing?.check_out ?? adoptOut ?? '',   // empty unless already recorded
        status:    existing?.status    ?? null,
        sched,
      }
    })
  }

  const [slotRows, setSlotRows] = useState(initSlotRows)
  const [note,     setNote]     = useState(record?.note ?? '')
  const [saving,   setSaving]   = useState(false)
  // a failed save SAYS so and keeps every edit on screen (it used to just stop the spinner)
  const [saveError, setSaveError] = useState(null)
  // ── AN UNSCHEDULED VISIT (owner, need-care list) ────────────────────────────────────
  // Someone with no roster today comes in for two hours and leaves. Until now this modal
  // could only say „დასვენება" at them: every input it has is built from schedule SLOTS,
  // and a day off has none — so the one thing an off day can produce was unrecordable, and
  // the only workaround was a schedule OVERRIDE in another tab, which asserts a different
  // fact (that they were rostered) to store this one.
  // The data model already allowed it: `attendance` carries top-level check_in/check_out
  // independent of any schedule, and both `attendanceStatusFor` and `actualMinutes` have
  // an explicit "no slots — working an unscheduled day" branch. Only the UI refused.
  const unscheduled = schedSlots.length === 0
  const [visit, setVisit] = useState({
    check_in:  record?.check_in  ?? '',
    check_out: record?.check_out ?? '',
  })

  function updateSlotRow(i, patch) {
    setSlotRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r))
  }

  function setSlotStatus(i, st) {
    setSlotRows(prev => prev.map((r, idx) => {
      if (idx !== i) return r
      return { ...r, status: r.status === st ? null : st }
    }))
  }

  // Delta only counts when BOTH check_in and check_out are real entered values
  const totalWorked = slotRows.reduce((sum, r) => {
    if (r.status === 'absent' || r.status === 'holiday') return sum
    if (!r.check_in || !r.check_out) return sum   // incomplete — don't count
    const m = diffMinutes(r.check_in, r.check_out)
    return sum + (m ?? 0)
  }, 0)
  const hasAnyComplete = slotRows.some(r => !r.status && r.check_in && r.check_out)
  const delta = hasAnyComplete && scheduled > 0 ? totalWorked - scheduled : null

  // Derive top-level status for backward compat
  function derivedTopStatus() {
    // 'present' has always meant IN THE OFFICE here, so a fully-remote day must derive
    // 'remote' or the editor quietly relocates the agent on every save (see
    // attendanceStatusFor). schedSlots is this day's roster, already in scope.
    const worked = schedSlots.length && schedSlots.every(s => s.location === 'home') ? 'remote' : 'present'
    if (slotRows.length === 0) return worked
    if (slotRows.every(r => r.status === 'absent'))  return 'absent'
    if (slotRows.every(r => r.status === 'holiday')) return 'holiday'
    return worked
  }

  async function handleSave() {
    setSaving(true); setSaveError(null)
    try {
      const topStatus = derivedTopStatus()
      const slots = slotRows.map(r => ({
        slot_index: r.slot_index,
        check_in:   r.status ? null : (r.check_in  || null),
        check_out:  r.status ? null : (r.check_out || null),
        status:     r.status ?? null,
      }))
      // Keep top-level check_in/check_out for backward compat: the FIRST worked slot that has
      // an arrival, and the LAST that has a departure.
      // ⚠️ It used to read both off `slotRows.find(r => !r.status)` — the first worked slot,
      // whatever it happened to hold. On a split day the departure lives on the EVENING slot, so
      // that expression read null from the morning row and nulled a real check_out on every save.
      // Single-slot days never showed it, which is why it survived: the two rules agree exactly
      // when there is one row.
      const workedRows = slotRows.filter(r => !r.status)
      // An unscheduled visit has no slot to hang times on, so the top-level columns ARE the
      // record — which is the shape `actualMinutes` already falls back to when `slots` is
      // empty. Nothing else in the payload differs: same status derivation, same note.
      const inTime  = unscheduled ? (visit.check_in  || null)
        : (workedRows.find(r => r.check_in)?.check_in || null)
      const outTime = unscheduled ? (visit.check_out || null)
        : ([...workedRows].reverse().find(r => r.check_out)?.check_out || null)
      await onSave({
        agent_id:  agent.id,
        date,
        status:    topStatus,
        check_in:  inTime,
        check_out: outTime,
        slots,
        confirmed: !!inTime,
        note:      note || null,
      }, record)
      onClose()
    } catch (e) {
      setSaveError(e?.message || 'error')
    } finally { setSaving(false) }
  }

  async function handleDelete() {
    if (record?.id) await onDelete(record.id)
    onClose()
  }

  const _dm = new Date(date + 'T12:00:00')
  const displayDate = `${t('fullDays')[(_dm.getDay()+6)%7]}, ${t('months')[_dm.getMonth()]} ${_dm.getDate()}`

  const inp = 'rounded-lg border border-input px-2 py-1.5 text-base sm:text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring w-full'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      {...backdropDismiss}>
      <div {...dialogProps} className="bg-card border border-border rounded-xl w-full max-w-lg shadow-xl outline-none">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 id={titleId} className="font-semibold text-sm">{agent.name} · {displayDate}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label={t('cancel')} className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>

        <div className="px-5 py-4 space-y-3">

          {/* Day off — no schedule. It still SAYS day off (that is the fact about the
              roster), and underneath offers the one thing that can still have happened. */}
          {unscheduled && (
            <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-3 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">{t('att_day_off')}</span>
                {/* Clears the visit rather than the record: pressing save with both blank is
                    how a mis-entered visit is withdrawn, and that needs no separate verb. */}
                {(visit.check_in || visit.check_out) && (
                  <button type="button" className="text-xs text-muted-foreground hover:text-destructive"
                    onClick={() => setVisit({ check_in: '', check_out: '' })}>{t('att_dayoff_visit_clear')}</button>
                )}
              </div>
              <div className="text-xs text-muted-foreground">{t('att_dayoff_visit_label')}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <TimeInput className={inp + ' w-auto'} value={visit.check_in}
                  onChange={v2 => setVisit(v => ({ ...v, check_in: v2 }))} />
                <span className="text-muted-foreground">–</span>
                <TimeInput className={inp + ' w-auto'} value={visit.check_out}
                  onChange={v2 => setVisit(v => ({ ...v, check_out: v2 }))} />
                {/* Same one-tap "now" the working cards give, on the same corrected clock —
                    the common case is recording this as it happens. */}
                <button type="button" className="text-xs text-primary hover:opacity-80"
                  onClick={() => setVisit(v => v.check_in ? { ...v, check_out: nowStr() } : { ...v, check_in: nowStr() })}>
                  {t('att_dayoff_visit_now')}
                </button>
                {(() => {
                  const m = diffMinutes(visit.check_in, visit.check_out)
                  return m != null && m > 0
                    ? <span className="text-xs text-muted-foreground tabular-nums">{fmtDuration(m)}</span>
                    : null
                })()}
              </div>
            </div>
          )}

          {/* Per-slot rows */}
          {slotRows.map((row, i) => {
            const isAbsent  = row.status === 'absent'
            const isHoliday = row.status === 'holiday'
            const isWorked  = !isAbsent && !isHoliday
            const slotMins  = diffMinutes(row.sched.start, row.sched.end) ?? 0
            const workedMins = isWorked && row.check_in && row.check_out ? (diffMinutes(row.check_in, row.check_out) ?? 0) : 0
            const slotDelta  = isWorked && row.check_in && row.check_out ? workedMins - slotMins : null

            return (
              <div key={i} className={`rounded-xl border px-3 py-3 space-y-2.5 transition-colors ${
                isAbsent  ? 'border-red-200 bg-red-50/40 dark:border-red-800/40 dark:bg-red-900/10'
                : isHoliday ? 'border-purple-200 bg-purple-50/40 dark:border-purple-800/40 dark:bg-purple-900/10'
                : 'border-border/60 bg-muted/20'
              }`}>

                {/* Slot header: scheduled time + duration + status badges */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${row.sched.location === 'office' ? 'bg-emerald-500' : 'bg-blue-400'}`} />
                    <span className="text-xs font-semibold tabular-nums text-foreground">
                      {row.sched.start.slice(0,5)} – {row.sched.end.slice(0,5)}
                    </span>
                    <span className="text-xs text-muted-foreground">{fmtDuration(slotMins)}</span>
                    {/* NAME the location, don't just colour a dot (owner 2026-08-03). The whole
                        remote-marked-as-office bug lived in a place nobody was told: the editor
                        showed a blue dot and every word around it said "office". A slot's
                        location is per-slot, so a split day says it twice, correctly. */}
                    <span className="text-xs text-muted-foreground">
                      <Icon name={row.sched.location === 'home' ? 'house' : 'building'} size={12} />{' '}
                      {row.sched.location === 'home' ? t('att_remote_short') : t('location_office')}
                    </span>
                  </div>
                  {/* Absent / Holiday toggle buttons */}
                  <div className="flex gap-1.5">
                    <button type="button" aria-pressed={!!isAbsent}
                      onClick={() => setSlotStatus(i, 'absent')}
                      className={`text-xs font-medium px-2 py-0.5 rounded-md border transition-colors ${
                        isAbsent
                          ? 'bg-red-500 text-white border-red-500'
                          : 'border-border text-muted-foreground hover:border-red-400 hover:text-red-500'
                      }`}>
                      {t('att_absent')}
                    </button>
                    <button type="button" aria-pressed={!!isHoliday}
                      onClick={() => setSlotStatus(i, 'holiday')}
                      className={`text-xs font-medium px-2 py-0.5 rounded-md border transition-colors ${
                        isHoliday
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'border-border text-muted-foreground hover:border-purple-400 hover:text-purple-500'
                      }`}>
                      {t('att_status_holiday')}
                    </button>
                  </div>
                </div>

                {/* Time inputs — hidden when absent/holiday */}
                {isWorked && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-muted-foreground">
                          {row.sched.location === 'home' ? t('att_start_time') : t('att_check_in')}
                        </label>
                        <button type="button" onClick={() => updateSlotRow(i, { check_in: nowStr() })}
                          className="text-xs font-semibold text-primary px-1.5 py-0.5 rounded bg-primary/10 hover:bg-primary/15 transition-colors">
                          {t('att_now')}
                        </button>
                      </div>
                      <TimeInput value={row.check_in} onChange={v => updateSlotRow(i, { check_in: v })}
                        placeholder={row.sched.start.slice(0,5)}
                        className={inp} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <label className="text-xs text-muted-foreground">
                            {row.sched.location === 'home' ? t('att_end_time') : t('att_check_out')}
                          </label>
                          {/* Show expected checkout when check_in entered but check_out still empty */}
                          {row.check_in && !row.check_out && (
                            <span className="text-xs text-muted-foreground/60">~{row.sched.end.slice(0,5)}</span>
                          )}
                        </div>
                        <button type="button" onClick={() => updateSlotRow(i, { check_out: nowStr() })}
                          className="text-xs font-semibold text-primary px-1.5 py-0.5 rounded bg-primary/10 hover:bg-primary/15 transition-colors">
                          {t('att_now')}
                        </button>
                      </div>
                      <TimeInput value={row.check_out} onChange={v => updateSlotRow(i, { check_out: v })}
                        placeholder={row.sched.end.slice(0,5)}
                        className={inp} />
                    </div>
                  </div>
                )}

                {/* Per-slot delta */}
                {slotDelta != null && Math.abs(slotDelta) > 0 && (
                  <div className={`text-xs font-medium tabular-nums ${slotDelta < 0 ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
                    {fmtDelta(slotDelta)} {t('att_vs_schedule')}
                  </div>
                )}
              </div>
            )
          })}

          {/* Total delta across all slots */}
          {schedSlots.length > 0 && delta != null && (
            <div className={`text-xs rounded-lg px-3 py-2 font-medium flex justify-between items-center ${
              delta < -15 ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
              : delta > 0  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
              : 'bg-muted text-muted-foreground'
            }`}>
              <span>{t('att_worked')} {fmtDuration(totalWorked)}</span>
              <span className="font-bold">{fmtDelta(delta)} {t('att_vs_schedule')}</span>
            </div>
          )}

          {/* Note */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">{t('att_note')}</label>
            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder={t('att_note_placeholder')}
              className="w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>

        {saveError && (
          <p role="alert" className="mx-5 mb-0 mt-1 rounded-lg border border-red-500/40 bg-red-500/5 px-3 py-2 text-xs text-red-600 dark:text-red-400">
            {t('att_save_failed')}
          </p>
        )}
        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-4 border-t border-border">
          {record && (
            <button onClick={handleDelete}
              className="text-sm font-medium text-destructive hover:bg-destructive/10 px-3 py-2 rounded-lg transition-colors">
              {t('att_clear_entry')}
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="text-sm font-medium px-4 py-2 rounded-lg border border-border hover:bg-accent transition-colors">{t('cancel')}</button>
          {/* 🔴 `schedSlots.length === 0` USED TO DISABLE THIS, which made the day-off visit
              editor above unusable: it offers the times, accepts them, shows the duration — and
              then Save is permanently grey, because a day off has no slots by definition.
              Owner-reported 2026-09-03: "I as admin cant set these day off peoples times."
              ⚠️ handleSave ALREADY handles this case (the `unscheduled` branch writes the times to
              the top-level columns, which for a day off ARE the record). The comment at
              `unscheduled` even says "Only the UI refused" — and this button was the UI refusing.
              Guarding on "is there anything to save" instead: a scheduled day always has slots, an
              unscheduled one needs a time entered, and clearing both is how a mis-entered visit is
              withdrawn (so an existing record must stay saveable when the fields are emptied). */}
          <button onClick={handleSave}
            disabled={saving || (unscheduled && !visit.check_in && !visit.check_out && !record?.check_in && !record?.check_out && !note)}
            className="text-sm font-medium px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-60 transition-opacity">
            {saving ? '…' : t('save')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Day cell in grid view ─────────────────────────────────────────────────────
function DayCell({ agent, date, record, onClick, scheduleHistory, overrideMap = {} }) {
  const scheduled = scheduledMinutes(agent, date, scheduleHistory, overrideMap)
  const isOff     = scheduled === 0
  const isFuture  = date > todayStr()
  const isToday   = date === todayStr()
  const status    = record?.status
  const confirmed = record?.confirmed
  const meta      = status ? STATUS_META[status] : null
  const isAssumed = !record || !confirmed
  const worked    = record ? actualMinutes(record, agent, scheduleHistory, overrideMap) : scheduled
  const delta     = worked != null ? worked - scheduled : 0
  // a recorded visit on a day with no shift is SHOWN (audit 2026-10-21 A01): it was a dash with no door to the editor
  const offVisit  = isOff && !!(record && (record.check_in || (Array.isArray(record.slots) && record.slots.some(x => x.check_in))))

  if (isOff && !offVisit) return (
    <div className="h-14 rounded-lg bg-muted/20 flex items-center justify-center">
      <span className="text-xs text-muted-foreground/30">—</span>
    </div>
  )
  if (isFuture) return (
    <div className={`h-14 rounded-lg border border-dashed border-border/40 flex items-center justify-center ${isToday ? 'ring-1 ring-primary/40' : ''}`}>
      <span className="text-xs text-muted-foreground/40">—</span>
    </div>
  )

  // ⚠️ `overflow-hidden` + `min-w-0` on the button below are load-bearing, not tidiness (owner
  // 2026-09-10, mobile: "we found truncated შვებ."). The cell is a FIXED h-14 square in a
  // 7-column grid, so on a phone each is ~44px wide — and its contents are Georgian words and
  // signed durations simply wider than that. Without them, `შვებულება` and `-1h 32m` painted
  // OUTSIDE the box, over the neighbouring day. A flex child defaults to min-width:auto, so
  // `truncate` on the children does nothing until the parent is allowed to shrink.
  //
  // 📌 And this comment sits HERE, above the return, because a `//` comment between JSX
  // ATTRIBUTES is a syntax error — which is exactly how the first version of this fix failed
  // the build. Third time in this repo; the note is the fix.
  return (
    <button onClick={onClick}
      className={`h-14 w-full min-w-0 overflow-hidden rounded-lg border text-left px-1.5 py-1 flex flex-col justify-between transition-all hover:shadow-md hover:scale-[1.02] ${
        isToday ? 'ring-1 ring-primary' : ''
      } ${
        !status ? 'border-amber-300/60 bg-amber-50/40 dark:border-amber-700/40 dark:bg-amber-900/10'
        : status === 'absent'  ? 'border-red-200 bg-red-50/50 dark:border-red-800/40 dark:bg-red-900/10'
        : status === 'holiday' ? 'border-purple-200 bg-purple-50/50 dark:border-purple-800/40 dark:bg-purple-900/10'
        : isAssumed            ? 'border-border/60 bg-muted/20'
        : 'border-border/40 bg-card'
      }`}>
      <div className="flex items-center justify-between">
        <span className="text-xs"><Icon name={meta ? meta.icon : 'help-circle'} size={13} /></span>
        {isAssumed && status !== 'absent' && status !== 'holiday' && (
          <span className="text-xs text-amber-500 font-bold"><Icon name="hourglass" /></span>
        )}
        {confirmed && <span className="text-xs text-emerald-500 font-bold"><Icon name="check" /></span>}
      </div>
      <div>
        {/* The word is TRUNCATED and the full one rides in `title` — the same short-label +
            tooltip idiom the market-report reasons and the contact badges use. Shrinking the
            font instead would make the longest word fit at one width and overflow at the next. */}
        {status === 'absent'  ? <div className="text-xs text-red-500 font-medium truncate" title={t('att_status_absent')}>{t('att_status_absent')}</div>
        : status === 'holiday' ? <div className="text-xs text-purple-500 font-medium truncate" title={t('att_status_holiday')}>{t('att_status_holiday')}</div>
        : (
          <>
            <div className="text-xs text-muted-foreground tabular-nums leading-none truncate">
              {record?.check_in ? record.check_in.slice(0,5) : <span className="opacity-30">{agent.schedule?.[dayKey(date)]?.start?.slice?.(0,5) || '?'}</span>}
            </div>
            {/* `-1h 32m` wrapped to two lines and pushed itself out of a h-14 cell. One line,
                clipped, with the value in the title.
                ⚠️ The comment is ABOVE the `&&`, not inside it: a JSX comment inside a JSX
                expression container is a syntax error too, the same family as one between
                attributes. Both forms broke this build in a row. (And a literal star-slash
                inside THIS comment closes it early — that broke the eslint gate on 2026-09-10.) */}
            {delta !== 0 && (
              <div className={`text-xs font-semibold tabular-nums whitespace-nowrap truncate ${delta < 0 ? 'text-red-500' : 'text-emerald-600'}`}
                title={fmtDelta(delta)}>
                {fmtDelta(delta)}
              </div>
            )}
          </>
        )}
      </div>
    </button>
  )
}

// ─── Month on a phone: one compact card per person (attendance audit 2026-10-21) ─────────────────────────────────
// The full month was every person's 31-cell grid stacked - ~12,700 px and ~7,000 elements on an admin phone. On a phone
// Month shows each person's totals in one line and their month as a strip of small coloured squares (state only),
// with "Show days" opening the same full DayCell grid as before. Nothing is hidden: every day stays one tap away.
function dayTone(agent, date, record, scheduleHistory, overrideMap, today) {
  const sched = scheduledMinutes(agent, date, scheduleHistory, overrideMap)
  const st = record ? deriveStatus(record) : null
  if (st === 'absent') return 'bg-red-500'
  if (st === 'holiday') return 'bg-purple-500'
  if (date > today) return sched ? 'border border-dashed border-border bg-transparent' : 'bg-muted/40'
  if (record?.confirmed) return 'bg-emerald-500'
  if (sched) return 'bg-amber-400/80'          // scheduled, nothing confirmed (assumed / pending)
  return 'bg-muted/40'                         // no shift
}
function MonthCompactCard({ agent, dates, agentAtt, scheduleHistory, overrideMap, lateThreshold, today, onOpenDay }) {
  const [open, setOpen] = useState(false)
  const sum = summarise(agent, dates, agentAtt, scheduleHistory, overrideMap, lateThreshold, today)
  return (
    <div className="bg-card border border-border rounded-xl px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-sm min-w-0 break-words">{agent.name}</span>
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
          className="shrink-0 text-xs text-primary px-2 py-1.5 rounded-md hover:bg-accent">
          {open ? t('att_hide_days') : t('att_show_days')}
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">
        {t('att_col_worked')} <span className="font-semibold text-foreground">{fmtDuration(sum.workedMins)}</span>
        {sum.assumedMins > 0 && <span className="text-amber-600 dark:text-amber-400"> ({t('att_incl_assumed').replace('{d}', fmtDuration(sum.assumedMins))})</span>}
        {' · '}{t('att_summary_late')} {sum.lateDays}
        {' · '}{t('att_summary_absent')} {sum.absentDays}
        {' · '}<Icon name="check" size={11} />{sum.confirmedDays}
      </p>
      {/* the month at a glance - decoration for sighted readers; the totals above and the day grid carry the facts */}
      <div className="mt-2 grid gap-[3px]" style={{ gridTemplateColumns: 'repeat(16, minmax(0, 1fr))' }} aria-hidden="true">
        {dates.map(d => (
          <span key={d} className={`h-2.5 rounded-[2px] ${dayTone(agent, d, agentAtt[d], scheduleHistory, overrideMap, today)} ${d === today ? 'ring-1 ring-primary' : ''}`} />
        ))}
      </div>
      {open && (
        <div className="mt-3 grid gap-1" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {dates.map(date => (
            <div key={date}>
              <div className="text-center text-[11px] text-muted-foreground leading-none mb-0.5">{Number(date.slice(8))}</div>
              <DayCell agent={agent} date={date} record={agentAtt[date]} scheduleHistory={scheduleHistory} overrideMap={overrideMap}
                onClick={() => onOpenDay(date)} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Agent summary row ────────────────────────────────────────────────────────
function AgentSummaryRow({ agent, dates, attendanceByDate, scheduleHistory, overrideMap = {}, lateThreshold = 5, today = null }) {
  // ⚠️ `lateThreshold` is `config.late_threshold_minutes` — the SAME value the Stats tab hands
  // to computeAgentStats. Owner 2026-09-10 stated the rule as "late is more than 5 minutes";
  // that number is a setting, not a literal, because two hard-coded thresholds would let this
  // card and the stats table disagree about the same morning.
  const sum = summarise(agent, dates, attendanceByDate, scheduleHistory, overrideMap, lateThreshold, today)
  const dc  = sum.deltaMins < -30 ? 'text-red-500' : sum.deltaMins > 0 ? 'text-emerald-600' : 'text-muted-foreground'
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 text-xs px-3 py-2 bg-muted/20 rounded-lg">
      <div><div className="text-muted-foreground mb-0.5">{t('att_col_scheduled')}</div><div className="font-semibold">{fmtDuration(sum.scheduledMins)}</div></div>
      <div><div className="text-muted-foreground mb-0.5">{t('att_col_worked')}</div><div className="font-semibold">{fmtDuration(sum.workedMins)}</div>
        {/* how much of Worked is assumed (no record / open check-in) and what is still ahead - audit 2026-10-21 A01 */}
        {sum.assumedMins > 0 && <div className="text-[11px] text-amber-600 dark:text-amber-400">{t('att_incl_assumed').replace('{d}', fmtDuration(sum.assumedMins))}</div>}
        {sum.plannedFutureMins > 0 && <div className="text-[11px] text-muted-foreground">{t('att_planned_ahead').replace('{d}', fmtDuration(sum.plannedFutureMins))}</div>}
      </div>
      <div><div className="text-muted-foreground mb-0.5">{t('att_col_delta')}</div><div className={`font-bold ${dc}`}>{fmtDelta(sum.deltaMins)}</div></div>
      <div><div className="text-muted-foreground mb-0.5">{t('att_summary_absent')}</div><div className="font-semibold">{sum.absentDays}d</div></div>
      <div><div className="text-muted-foreground mb-0.5">{t('att_col_confirmed')}</div><div className="font-semibold">{sum.confirmedDays}d <span className="text-amber-500">~{sum.assumedDays}d</span></div></div>
      <div><div className="text-muted-foreground mb-0.5">{t('att_summary_remote')}</div><div className="font-semibold">{sum.remoteDays}d</div></div>
      {/* Owner 2026-09-10, for exactly this card in the week and month views: "we need to see how
          many days have been late… and how many minutes have been late. And how many minutes
          overtime. How many days გაცდენა." The absent count was already here (გამოცხ.); these
          two are new, and both carry the DAYS and the MINUTES because either alone is
          unactionable — three days late could be nine minutes or three hours. */}
      <div><div className="text-muted-foreground mb-0.5">{t('att_summary_late')}</div>
        <div className={`font-semibold ${sum.lateDays ? 'text-amber-500' : ''}`}>
          {sum.lateDays ? <>{sum.lateDays}d <span className="text-xs font-normal opacity-70">{fmtDuration(sum.lateMins)}</span></> : '0d'}
        </div></div>
      <div><div className="text-muted-foreground mb-0.5">{t('att_summary_overtime')}</div>
        <div className={`font-semibold ${sum.overtimeMins ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
          {sum.overtimeMins ? fmtDuration(sum.overtimeMins) : '0'}
        </div></div>
    </div>
  )
}


// ─── Agent single-select dropdown (custom, matches dark theme) ───────────────
function AgentSingleSelect({ value, onChange, agents, allLabel }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  const current = value === 'all' ? allLabel : agents.find(a => a.id === value)?.name ?? allLabel
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 rounded-xl border border-input px-3 py-2 text-sm bg-background hover:border-primary/50 transition-colors min-w-[180px] justify-between">
        <span className="truncate">{current}</span>
        <span className="text-xs shrink-0">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div ref={keepInView} className="absolute top-full mt-1 left-0 z-50 bg-card border border-border rounded-xl shadow-xl py-1 min-w-full max-h-64 overflow-y-auto">
          {[{ id: 'all', name: allLabel }, ...agents].map(a => (
            <button key={a.id} onClick={() => { onChange(a.id); setOpen(false) }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center gap-2 ${value === a.id ? 'text-primary font-medium' : ''}`}>
              {value === a.id && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
              {value !== a.id && <span className="w-1.5 h-1.5 shrink-0" />}
              {a.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Day view — all agents as compact grid for one specific date ───────────────
// ⚠️ `viewer` PUTS THIS GRID IN FRONT OF AN AGENT (owner 2026-09-02: "this is what agents
// sees and can it see full cars list? same UI design just limited functionality just one day").
// It REVERSES the call MyAttendance.jsx was built on — that file argued a narrowed roster was
// the wrong shape and shipped a single card instead. The owner has ruled the other way, and the
// ruling is better: an agent seeing who else is in is the point of an attendance board.
//   viewer = null            → management. Everything as before; nothing below changes.
//   viewer = { agentId }     → an agent. Three things come off, each for its own reason:
//     • the hover popover, which carries a COLLEAGUE'S WORK PHONE and district. Management
//       needs it to chase a late agent; "limited functionality" is not a reason to publish
//       every agent's number to every agent, so it stays management-only until asked for.
//     • the modal on every card, their own included — an agent may not edit anyone's day,
//       and their own presses are write-once by the owner's earlier ruling.
//     • the quick In/Out on OTHER people's cards. Their own keeps them, routed through
//       markOwnArrival/markOwnDeparture rather than onSave, because RLS (0003) refuses an
//       agent's direct write to `attendance` — the button would fail, correctly and silently.
// ⚠️ MODULE SCOPE since 2026-10-22 (attendance audit): AgentBox was defined INSIDE DayView, so every 30 s clock render
// created a new component type and React remounted every card - and a keyboard user lost focus mid-card. Everything
// it read from DayView arrives in `ctx`; the helpers below are pure.
// Helper: minutes from midnight for a HH:MM string
function toMins(hhmm) {
  if (!hhmm) return null
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

// Helper: format a minute-delta as "Xh Ym" or "Ym"
function fmtCountdown(mins) {
  const abs = Math.abs(mins)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  if (h === 0) return `${m}m`
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

// Minute-of-day → "HH:MM"
function fmtMins(mins) { return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}` }

// Trim seconds from stored times e.g. "09:00:00" → "09:00"
function trimTime(t) { return t ? t.slice(0, 5) : t }

function AgentBox({ agent, ctx }) {
  const { restricted, viewer, date, attendanceByDate, scheduleHistory, overrideMap, onOpenModal, onSave, lang, isToday, nowD, onMeetingByAgent, markersFailed, lateThreshold } = ctx
  const rec        = attendanceByDate[agent.id]?.[date]
  const schedLoc   = scheduledLocation(agent, date, scheduleHistory, overrideMap)
  const rawStatus  = rec?.status
  const scheduled  = scheduledMinutes(agent, date, scheduleHistory, overrideMap)
  const isOff      = scheduled === 0 && !rec
  const isFuture   = date > todayStr()

  // Get first/last slot scheduled times for live countdown
  const slots = scheduledSlotsOnDate(agent, date, scheduleHistory, overrideMap)
  const firstSlotStart = trimTime(slots[0]?.start) ?? null
  const lastSlotEnd    = trimTime(slots[slots.length - 1]?.end) ?? null

  // Check_in / check_out from record (real values only)
  const checkIn  = trimTime(rec?.check_in  || rec?.slots?.find(s => s.check_in)?.check_in  || null)
  const checkOut = trimTime(rec?.check_out || rec?.slots?.find(s => s.check_out)?.check_out || null)

  // Ignore stale present/remote status if there's no actual check-in — treat as pending
  const status  = (rawStatus === 'present' || rawStatus === 'remote') && !checkIn ? null : rawStatus

  // confirmed only counts if there's a real check-in (guards against stale DB flags)
  const confirmed  = rec?.confirmed && !!checkIn

  // Live status line — only meaningful for today
  function liveStatus() {
    if (!isToday || isOff || isFuture || status === 'absent' || status === 'holiday') return null

    const nowMins   = tzHour(nowD) * 60 + tzMinute(nowD)
    const startMins = toMins(firstSlotStart)
    const endMins   = toMins(lastSlotEnd)
    const inMins    = toMins(checkIn)

    // Already checked out
    if (checkIn && checkOut) return null

    // Has checked in — show "leaves at ~18:00 (in Xh Ym)"
    if (checkIn && !checkOut && endMins != null) {
      const minsLeft = endMins - nowMins
      if (minsLeft > 0) {
        return (
          <div className="text-xs text-blue-400 tabular-nums mt-0.5">
            {t('att_live_leaves')}{lastSlotEnd} <span className="font-semibold text-blue-300">({fmtCountdown(minsLeft)})</span>
          </div>
        )
      } else {
        return (
          <div className="text-xs text-amber-400 tabular-nums mt-0.5">
            {t('att_live_due_out')} {lastSlotEnd} <span className="font-semibold">({fmtCountdown(Math.abs(minsLeft))} {t('att_live_ago')})</span>
          </div>
        )
      }
    }

    // Not checked in yet
    if (!checkIn && startMins != null) {
      const minsUntil = startMins - nowMins
      if (minsUntil > 0 && minsUntil <= 30) {
        return (
          <div className="text-xs text-amber-400 font-semibold mt-0.5 animate-pulse">
            <Icon name="clock" size={12} /> {t('att_live_in')} {fmtCountdown(minsUntil)}
          </div>
        )
      } else if (minsUntil > 30) {
        return (
          <div className="text-xs text-muted-foreground/60 mt-0.5">
            {t('att_live_due')} {firstSlotStart} <span className="font-semibold">({fmtCountdown(minsUntil)})</span>
          </div>
        )
      } else if (onMeeting) {
        // Not late — booked at a viewing. Says since when, and how long ago that was, so
        // the manager knows they are at a meeting rather than missing.
        return (
          <div className="text-xs text-blue-400 font-semibold mt-0.5">
            <Icon name="calendar" size={11} /> {t('att_live_on_meeting')} {fmtMins(onMeeting.minute)} <span className="font-normal">({fmtCountdown(nowMins - onMeeting.minute)} {t('att_live_ago')})</span>
          </div>
        )
      } else if (isUnresolved) {
        return (
          <div className="text-xs text-amber-600 dark:text-amber-400 font-semibold mt-0.5">
            {t('att_live_unresolved')} · {t('att_live_scheduled')} {firstSlotStart}–{lastSlotEnd}
          </div>
        )
      } else if (isLate) {
        return (
          <div className="text-xs text-red-400 font-semibold mt-0.5">
            {t('att_live_no_checkin')} · {t('att_live_scheduled')} {firstSlotStart}–{lastSlotEnd}
            {markersFailed && <span className="font-normal"> · {t('att_live_meetings_unknown')}</span>}
          </div>
        )
      } else {
        // inside the grace minutes: still just "due"
        return (
          <div className="text-xs text-amber-500 mt-0.5">
            {t('att_live_due')} {firstSlotStart}
          </div>
        )
      }
    }
    return null
  }

  // Worked delta — only when both times entered
  const worked = checkIn && checkOut ? diffMinutes(checkIn, checkOut) : null
  const delta  = worked != null && scheduled > 0 ? worked - scheduled : null

  // Is this agent actually late? (no check-in, shift has started, today only)
  const nowMins     = tzHour(nowD) * 60 + tzMinute(nowD)
  const startMins   = toMins(firstSlotStart)
  // A live meeting the agent is ON, already begun: the reason they are not at their desk
  // is known, so it is not lateness (owner 2026-08-21).
  const onMeeting   = isToday && !checkIn ? (onMeetingByAgent.get(agent.id) || null) : null
  // ⚠️ NO CHECK-IN IS NOT A MEASURED LATENESS (attendance audit 2026-10-21, A04). It used to read "late" one minute
  // after the start and keep counting into the night ("12h 39m late" for an 11:00–16:30 shift). Now: past the start
  // + the SAME late threshold the week/month summary uses → "no check-in recorded"; past the shift END →
  // "attendance unresolved". Neither claims an arrival time or an absence.
  const endMins     = toMins(lastSlotEnd)
  const pendingNoCheckIn = !status && !isOff && !isFuture && isToday && startMins != null && !checkIn && !onMeeting
  const isLate      = pendingNoCheckIn && nowMins > startMins + lateThreshold && (endMins == null || nowMins <= endMins)
  const isUnresolved = pendingNoCheckIn && endMins != null && nowMins > endMins

  // Just checked in (today, <3 min ago) — hold off on showing the Out button
  const checkInMins = toMins(checkIn)
  const justArrived = isToday && checkIn && !checkOut && checkInMins != null && (nowMins - checkInMins) < 3

  // Location icon for scheduled agents (office or remote)
  const locIcon = schedLoc === 'home' ? 'house' : schedLoc !== 'off' ? 'building' : null

  const borderColor = !status && !isOff && !isFuture
    ? isLate
      ? 'border-red-500 bg-red-50/30 dark:border-red-500/70 dark:bg-red-900/20'
      : onMeeting
        ? 'border-blue-400 bg-blue-50/30 dark:border-blue-500/60 dark:bg-blue-900/20'
        : 'border-amber-400 bg-amber-50/50 dark:border-amber-500/60 dark:bg-amber-900/20'
    : status === 'absent'  ? 'border-red-400   bg-red-50/50 dark:border-red-600/60 dark:bg-red-900/20'
    : status === 'holiday' ? 'border-purple-400 bg-purple-50/50 dark:border-purple-500/60 dark:bg-purple-900/20'
    : status === 'present' ? 'border-emerald-400 bg-emerald-50/30 dark:border-emerald-600/60 dark:bg-emerald-900/20'
    : status === 'remote'  ? 'border-blue-400   bg-blue-50/30 dark:border-blue-500/60 dark:bg-blue-900/20'
    /* ⚠️ No `opacity-50` here, and the alphas are pre-halved instead. This card contains
       the hover popover, and `opacity` applies to every descendant with no way for a
       child to opt back out — a dimmed card dims the popover with it and, being a
       stacking context exactly like the transform noted below, traps its z-30 under every
       later card in the grid. Same defect as the past-interview row in CandidatesTab.
       border-border/30 and bg-muted/10 composited through opacity 0.5 are 0.15 and 0.05
       (measured, not derived), so the card is unchanged; content carries `dim` instead.
       📌 This branch is UNREACHABLE today, so do not go looking for the symptom: AgentBox
       has one call site (`working.map`, ~line 923) filtered on
       `scheduledMinutes(...) > 0`, while isOff needs that same expression `=== 0` — a
       day-off agent renders in the strip below the grid, never as a card. Fixed anyway
       because the trap arms itself the moment anyone renders one. */
    : isOff                ? 'border-border/[0.15] bg-muted/5'
    : 'border-border/50 bg-card'

  const live = liveStatus()

  // The card is a role="button" DIV, not a <button>, because it contains the quick
  // In/Out <button>s below — a button nested in a button is invalid HTML (React warns,
  // and browsers may drop clicks). Div + role/tabIndex/keyboard keeps identical behavior
  // and accessibility while letting the inner actions stay real buttons.
  // ⚠️ AN OFF DAY OPENS NOW (owner 2026-09-02: "may we show this part fully? … no ability
  // to open modal"). It was inert because the modal had nothing to offer a day with no
  // slots; since the unscheduled-visit row exists, the card is the door to it. A FUTURE
  // day stays shut — there is nothing to record about a day that has not happened.
  // In restricted mode NOTHING opens: an agent may not edit a day, theirs included.
  const isMe = !restricted || agent.id === viewer.agentId
  const cardActive = !isFuture && !restricted
  // Dims the day-off card's CONTENT. It must never move onto the card itself — see the
  // borderColor note above; the popover is a descendant and would go with it.
  const dim = isOff ? 'opacity-50' : ''
  const openModal  = () => cardActive && onOpenModal({ agent, date, record: rec })
  return (
    <div
      role="button"
      tabIndex={cardActive ? 0 : -1}
      aria-disabled={!cardActive}
      onClick={openModal}
      /* e.target === e.currentTarget (audit 2026-10-21): Enter/Space on the quick In/Out buttons inside BUBBLED here,
         was preventDefault-ed and opened the editor instead of checking the agent in */
      onKeyDown={e => { if (cardActive && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openModal() } }}
      /* ⚠️ hover:z-20 is what makes the popover below actually visible, and it is not
         cosmetic. `hover:scale-[1.01]` applies a TRANSFORM, and a transform creates a
         stacking context — so on hover this card became a context with z-index auto and
         its z-30 popover was trapped inside it, painted UNDER every later card in the
         grid. It read as a see-through tooltip; it was the next card drawn on top of it.
         Raising the card itself lifts the whole context above its siblings. */
      className={`group relative hover:z-20 rounded-xl border p-3 text-left transition-all ${cardActive ? 'cursor-pointer hover:shadow-sm hover:scale-[1.01]' : 'cursor-default'} ${borderColor}`}
    >
      {/* Who to call and where they cover, on hover (owner 2026-08-03). Both answer the
          question this card provokes — someone is late, or remote, or absent, and the next
          act is to ring them. A real popover rather than a title attribute so the number
          can be SELECTED: a tooltip you cannot copy from is a number you retype by eye.
          Rendered only when there is something to say; hidden on touch (no hover), where
          the card's own modal is the route.
          ⚠️ This tab is admin/manager-only (permissions.js: agents have no tab.attendance),
          which is why a work number may be shown here at all. */}
      {!restricted && (agent.phone_work || agent.district) && (
        <div className="pointer-events-none group-hover:pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity
                        absolute z-30 left-3 right-3 top-full mt-1 rounded-lg border border-border bg-popover shadow-xl px-2.5 py-1.5
                        text-xs space-y-0.5 hidden md:block"
             onClick={e => e.stopPropagation()}>
          {agent.phone_work && (
            <div className="flex items-center gap-1.5 whitespace-nowrap">
              <Icon name="phone" size={11} />
              <a href={`tel:${agent.phone_work}`} className="font-medium tabular-nums hover:underline select-text">
                {fmtPhone(agent.phone_work)}
              </a>
            </div>
          )}
          {agent.district && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Icon name="map-pin" size={11} /> <span className="select-text">{agent.district}</span>
            </div>
          )}
        </div>
      )}
      {/* Name + status icon */}
      <div className={`flex items-start justify-between gap-1 mb-1.5 ${dim}`}>
        <div className="min-w-0">
          <div className="text-xs font-semibold truncate">{agent.name}</div>
          <div className="flex items-center gap-1">
            <div className="text-xs text-muted-foreground truncate">{agent.role}</div>
            {/* Late indicator */}
            {/* Late indicator. (In the agent view only their own card reaches this point —
                see the `restricted && !isMe` return above.) */}
            {isLate && isToday && (
              <span className="text-xs font-bold text-red-400 shrink-0">{t('att_no_checkin_short')}</span>
            )}
            {isUnresolved && (
              <span className="text-xs font-bold text-amber-600 dark:text-amber-400 shrink-0">{t('att_unresolved_short')}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          {/* For pending (no status): show location icon + state icon */}
          {!status && !isOff && !isFuture && (
            <>
              <span className="text-sm leading-none">{locIcon && <Icon name={locIcon} size={14} />}</span>
              <span className={`text-xs font-bold ${isLate ? 'text-red-400' : onMeeting ? 'text-blue-400' : 'text-amber-500'}`}>
                <Icon name={isLate ? 'alert-triangle' : onMeeting ? 'calendar' : 'hourglass'} size={12} />
              </span>
            </>
          )}
          {/* For confirmed status: STATUS_META icon already encodes location */}
          {status && <span className="text-sm">
            <Icon name={STATUS_META[status]?.icon} size={14} />
          </span>}
          {confirmed && <span className="text-xs text-emerald-500"><Icon name="check" /></span>}
        </div>
      </div>

      {/* Times / status text */}
      {isOff ? (
        // A visit recorded on an off day is the whole reason this card is clickable, so it
        // shows the times rather than repeating "day off" over data.
        checkIn ? (
          <div className="text-xs tabular-nums">
            <span className="font-medium text-emerald-600 dark:text-emerald-400">
              {checkIn}{checkOut ? `–${checkOut}` : <span className="text-muted-foreground/50"> –?</span>}
            </span>
            <span className="text-muted-foreground/50 ml-1">· {t('att_day_off')}</span>
          </div>
        ) : (
        <div className="text-xs text-muted-foreground">{t('att_day_off')}</div>
        )
      ) : isFuture ? (
        <div className="text-xs text-muted-foreground/40">—</div>
      ) : status === 'absent' ? (
        <div className="text-xs text-red-500 font-medium">{t('att_status_absent')}</div>
      ) : status === 'holiday' ? (
        <div className="text-xs text-purple-500 font-medium">{t('att_status_holiday')}</div>
      ) : (
        <div className="space-y-0.5">
          <div className="text-xs tabular-nums">
            {checkIn ? (
              <span className="font-medium text-foreground">
                {checkIn}{checkOut ? `–${checkOut}` : <span className="text-muted-foreground/50"> –?</span>}
              </span>
            ) : (
              <span className="text-muted-foreground/50">
                {scheduledMinutes(agent, date, scheduleHistory, overrideMap) > 0
                  ? `${t('att_scheduled_label')} ${fmtDuration(scheduledMinutes(agent, date, scheduleHistory, overrideMap))}`
                  : '—'}
              </span>
            )}
          </div>
          {live}
          {delta != null && Math.abs(delta) > 5 && (
            <div className={`text-xs font-semibold ${delta < 0 ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {fmtDelta(delta)}
            </div>
          )}
        </div>
      )}

      {/* Quick check-in / check-out buttons — today only.
          🔴 A DAY OFF NO LONGER HIDES THEM FOR THE AGENT THEMSELVES (owner 2026-09-03: "allow
          these people to be able to select I came from there even if their shift is not to
          select I came when they open it from this IP"). Someone who comes in on their day off
          is exactly the case the day-off VISIT record exists for, and refusing the button meant
          the only way to record it was to ask a manager to type it in afterwards.
          ⚠️ The office-IP rule is UNCHANGED and is not enforced here — it lives in SQL (T30), so
          showing the button cannot grant anything: a press from outside the office is refused by
          the same rule that refuses it on a working day. This widens who may ASK, never who may
          write. ⚠️ Still `isMe`: it is not a door onto a colleague's day. */}
      {isToday && !isFuture && (!isOff || (restricted && isMe)) && status !== 'absent' && status !== 'holiday' && isMe && (
        <div className="flex gap-1 mt-1.5" onClick={e => e.stopPropagation()}>
          {!checkIn && (
            <button
              onClick={async e => { e.stopPropagation()
                const t2 = tzTimeStr(now())   // Tbilisi wall-clock from the server-corrected instant
                const slots2 = scheduledSlotsOnDate(agent, date, scheduleHistory, overrideMap)
                // The ROSTER says where they are, not the button (see attendanceStatusFor).
                // An existing record's status still wins — a manager who set it deliberately
                // must not be overruled by the default.
                await onSave({ agent_id: agent.id, date, status: rec?.status || attendanceStatusFor(agent, date, scheduleHistory, overrideMap), check_in: t2, check_out: rec?.check_out || null,
                  slots: slots2.map((s,i) => ({ slot_index: i, check_in: i === 0 ? t2 : rec?.slots?.[i]?.check_in || null, check_out: rec?.slots?.[i]?.check_out || null, status: null })),
                  confirmed: true, note: rec?.note || null }, rec)
              }}
              className={pill('none', { door: true, extra: 'font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/30 transition-colors border border-emerald-500/30' })}>
              {/* "In"/"Out" is office vocabulary — an agent working from home never
                  arrives anywhere (owner 2026-08-03). Remote days say started/ended, so
                  the button names the act it actually performs. */}
              {/* ⚠️ FIRST PERSON WHEN IT IS YOUR OWN CARD. The grid's labels are a
                  MANAGER speaking about someone else — "შემოვიდა" is *he came in* — and an
                  agent pressing that about themselves reads as marking a colleague. The
                  agent board reuses this button, so the sentence has to change with the
                  speaker, not just the permission. */}
              <Icon name="check" /> {restricted ? t('my_att_arrive')
                : schedLoc === 'home' ? t('att_started') : t('att_in')}
            </button>
          )}
          {checkIn && !checkOut && (
            justArrived ? (
              <span className={`${COUNTER} font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30`}>
                {t('att_just_arrived')}
              </span>
            ) : (
              <button
                onClick={async e => { e.stopPropagation()
                  const t2 = tzTimeStr(now())   // Tbilisi wall-clock from the server-corrected instant
                  const slots2 = scheduledSlotsOnDate(agent, date, scheduleHistory, overrideMap)
                  const lastIdx = slots2.length - 1
                  await onSave({ agent_id: agent.id, date, status: rec?.status || attendanceStatusFor(agent, date, scheduleHistory, overrideMap), check_in: rec?.check_in || null, check_out: t2,
                    slots: (rec?.slots || slots2.map((s,i) => ({ slot_index: i, check_in: null, check_out: null, status: null }))).map((s,i) => i === lastIdx ? { ...s, check_out: t2 } : s),
                    confirmed: true, note: rec?.note || null }, rec)
                }}
                className={pill('none', { door: true, extra: 'font-semibold bg-blue-500/15 text-blue-500 hover:bg-blue-500/30 transition-colors border border-blue-500/30' })}>
                <Icon name="check" /> {restricted ? t('my_att_leave')
                  : schedLoc === 'home' ? t('att_ended') : t('att_out')}
              </button>
            )
          )}
        </div>
      )}
    </div>
  )
}

export function DayView({ agents, date, attendanceByDate, onOpenModal, onSave, lang, scheduleHistory, overrideMap = {}, expectedCandidates = [], onCandidateShowedUp, onCandidateNoShow, viewer = null, lateThreshold = 5 }) {
  const restricted = !!viewer
  const _d = new Date(date + 'T12:00:00')
  const dayLabel = `${t('fullDays')[(_d.getDay()+6)%7]}, ${t('months')[_d.getMonth()]} ${_d.getDate()}, ${_d.getFullYear()}`

  // Split agents by their status for this day
  const inOffice = agents.filter(a => {
    const rec = attendanceByDate[a.id]?.[date]
    const loc = scheduledLocation(a, date, scheduleHistory, overrideMap)
    const status = rec?.status || (loc !== 'off' ? 'assumed' : 'off')
    return status === 'present' || (status === 'assumed' && loc !== 'off' && loc !== 'home')
  })
  const remote = agents.filter(a => {
    const rec = attendanceByDate[a.id]?.[date]
    const loc = scheduledLocation(a, date, scheduleHistory, overrideMap)
    const status = rec?.status || (loc === 'home' ? 'assumed' : 'off')
    return status === 'remote' || (status === 'assumed' && loc === 'home')
  })
  const absent  = agents.filter(a => attendanceByDate[a.id]?.[date]?.status === 'absent')
  const off     = agents.filter(a => {
    const rec = attendanceByDate[a.id]?.[date]
    if (rec) return false
    return scheduledLocation(a, date, scheduleHistory, overrideMap) === 'off'
  })

  // Live clock — SERVER-corrected instant, ticks every 30s only when viewing today. Read office-tz
  // minutes-of-day off it (tzHour/tzMinute), never the device's getHours()/getMinutes().
  const isToday = date === todayStr()
  const nowD = useClock(isToday ? 30000 : 0)

  // ── Who is OUT ON A MEETING right now (owner 2026-08-21) ──────────────────
  // An agent with no check-in after shift start read as "late" even when the system
  // itself had them booked at a viewing at 11:00. The same marker feed the activity rail
  // draws (when / status / whose — nothing else) answers it: a live meeting (scheduled or
  // pending, i.e. not yet resolved) whose instant has passed, for an agent who is ON it.
  // ⚠️ `agent_ids` is the GOING set since 2026-08-21 (attending=true, with a fallback to the
  // involved set only when nobody has answered), so a colleague merely in the deal does not
  // get an alibi for somebody else's viewing.
  const [meetingMarkers, setMeetingMarkers] = useState([])
  // ⚠️ (attendance audit 2026-10-21) The day window is the TBILISI day (it was the device's midnight), the markers are
  // re-read every 5 minutes while today is open (a cancelled or moved viewing used to keep excusing the agent all
  // day), and a failed read is remembered as "unknown" - it must not turn into evidence either way.
  const [markersFailed, setMarkersFailed] = useState(false)
  const [markersTick, setMarkersTick] = useState(0)
  useEffect(() => {
    if (!isToday) return
    const id = setInterval(() => { if (document.visibilityState === 'visible') setMarkersTick(n => n + 1) }, 5 * 60_000)
    return () => clearInterval(id)
  }, [isToday])
  useEffect(() => {
    if (!isToday) { setMeetingMarkers([]); return }
    let gone = false
    const win = tbilisiDayWindow(date)
    getMeetingMarkers({ from: win.startIso, to: win.endIso })
      .then(rows => { if (!gone) { setMeetingMarkers(rows || []); setMarkersFailed(false) } })
      .catch(() => { if (!gone) setMarkersFailed(true) })   // core outage → keep the last list, never a blank tab
    return () => { gone = true }
  }, [date, isToday, markersTick])
  // agent id → the latest live meeting that has already started (office-tz minute of day)
  const MEETING_ALIBI_MINS = 3 * 60
  const onMeetingByAgent = useMemo(() => {
    const m = new Map()
    const nowMins = tzHour(nowD) * 60 + tzMinute(nowD)
    for (const mk of meetingMarkers) {
      // MEETING_ACTIVE, not a pair: an agent sitting in a `binder` meeting (money on the
      // table) or one marked `rescheduled` is as much 'on a meeting' as a scheduled one.
      if (!mk.at || !MEETING_ACTIVE.includes(mk.status)) continue
      const d = new Date(mk.at)
      const minute = tzHour(d) * 60 + tzMinute(d)
      if (minute > nowMins) continue
      // a viewing excuses a missing check-in for a bounded time after it starts, not for the rest of the evening
      if (nowMins - minute > MEETING_ALIBI_MINS) continue
      for (const aid of (mk.agent_ids || [])) {
        const prev = m.get(aid)
        if (!prev || prev.minute < minute) m.set(aid, { minute, id: mk.id })
      }
    }
    return m
  }, [meetingMarkers, nowD])



  // Effective status — ignores stale present/remote saved without a check-in
  function effectiveStatus(a) {
    const rec = attendanceByDate[a.id]?.[date]
    const raw = rec?.status
    const ci  = rec?.check_in || rec?.slots?.find(s => s.check_in)?.check_in || null
    return (raw === 'present' || raw === 'remote') && !ci ? null : raw
  }

  // Summary counts
  const totalScheduled  = agents.filter(a => scheduledMinutes(a, date, scheduleHistory, overrideMap) > 0).length
  const confirmedCount  = agents.filter(a => {
    const rec = attendanceByDate[a.id]?.[date]
    const ci  = rec?.check_in || rec?.slots?.some(s => s.check_in)
    return rec?.confirmed && ci
  }).length
  const absentCount     = agents.filter(a => effectiveStatus(a) === 'absent').length
  const notEnteredCount = agents.filter(a => {
    const isScheduled = scheduledMinutes(a, date, scheduleHistory, overrideMap) > 0
    const es  = effectiveStatus(a)
    const rec = attendanceByDate[a.id]?.[date]
    const ci  = rec?.check_in || rec?.slots?.some(s => s.check_in)
    const resolved = es === 'absent' || es === 'holiday' || (rec?.confirmed && ci)
    return isScheduled && !resolved && date <= todayStr()
  }).length

  // Overdue checkout: checked in, no checkout, and either
  //  - viewing a past date (definitely overdue), or
  //  - viewing today and scheduled end time has passed
  const nowMinsGlobal = tzHour(nowD) * 60 + tzMinute(nowD)
  const isPast = date < todayStr()
  const overdueAgents = (isToday || isPast) ? agents.filter(a => {
    const rec = attendanceByDate[a.id]?.[date]
    const ci  = rec?.check_in || rec?.slots?.find(s => s.check_in)?.check_in || null
    const co  = rec?.check_out || rec?.slots?.find(s => s.check_out)?.check_out || null
    if (!ci || co) return false
    if (isPast) return true
    // ⚠️ the EFFECTIVE schedule of that date (history + one-off override), the same one the cards use (attendance audit
    // 2026-10-21, A05) - the base weekly template called an override shift running to 20:00 "overdue" at 19:00
    const slots = scheduledSlotsOnDate(a, date, scheduleHistory, overrideMap)
    const lastEnd = slots[slots.length - 1]?.end
    if (!lastEnd) return false
    const [h, m] = lastEnd.split(':').map(Number)
    return nowMinsGlobal > h * 60 + m
  }) : []
  const overdueCount = overdueAgents.length

  // The bulk tool stays (an authorized manager shortcut) but now: the effective schedule for each person, a preview that
  // names everyone and the end time each one gets, a note saying the exit was FILLED, not observed, a busy state, and
  // a per-person result instead of a silent partial batch.
  const [bulkBusy, setBulkBusy] = useState(false)
  async function handleBulkCheckout() {
    if (!overdueAgents.length || bulkBusy) return
    const plan = overdueAgents.map(a => {
      const slots = scheduledSlotsOnDate(a, date, scheduleHistory, overrideMap)
      return { a, slots, lastEnd: slots[slots.length - 1]?.end?.slice(0, 5) || null }
    })
    const skipped = plan.filter(x => !x.lastEnd)
    const todo = plan.filter(x => x.lastEnd)
    if (!todo.length) { alert(t('att_bulk_none')); return }
    const preview = todo.map(x => `• ${x.a.name} → ${x.lastEnd}`).join('\n')
    if (!confirm(`${t('att_bulk_confirm')}\n\n${preview}${skipped.length ? `\n\n${t('att_bulk_skipped')}: ${skipped.map(x => x.a.name).join(', ')}` : ''}`)) return
    setBulkBusy(true)
    const failed = []
    for (const { a, slots, lastEnd } of todo) {
      const rec = attendanceByDate[a.id]?.[date]
      try { await onSave({
        agent_id:  a.id,
        date,
        status:    rec?.status || attendanceStatusFor(a, date, scheduleHistory, overrideMap),
        check_in:  rec?.check_in || null,
        check_out: lastEnd,
        slots:     (rec?.slots || slots.map((s,i) => ({ slot_index: i, check_in: null, check_out: null, status: null }))).map((s, i) => {
          const isLast = i === slots.length - 1
          return isLast ? { ...s, check_out: lastEnd } : s
        }),
        confirmed: true,
        // provenance: this end time was filled from the schedule by a manager, not recorded on exit
        note:      [rec?.note, t('att_bulk_note')].filter(Boolean).join(' · '),
      }, rec) } catch { failed.push(a.name) }
    }
    setBulkBusy(false)
    if (failed.length) alert(`${t('att_bulk_failed')}: ${failed.join(', ')}`)
  }

  const agentBoxCtx = { restricted, viewer, date, attendanceByDate, scheduleHistory, overrideMap, onOpenModal, onSave, lang, isToday, nowD, onMeetingByAgent, markersFailed, lateThreshold }
  return (
    <div className="space-y-4">
      {/* Day header */}
      <div className="bg-card border border-border rounded-xl px-4 py-3 space-y-2">
        {/* Row 1: date + bulk button. ⚠️ The row wraps and the button may break its own line (owner screenshot
            2026-10-22, iPhone): a never-wrapping Georgian label ("ყველა ვადაგადაცილებულის გასვლა (4)") made the
            whole Attendance page wider than the phone, cutting every card at the left edge. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 font-semibold text-sm">{dayLabel}</div>
          {overdueCount > 0 && (
            <button type="button" onClick={handleBulkCheckout} disabled={bulkBusy}
              className="flex max-w-full items-center gap-1.5 text-left text-xs font-semibold px-3 py-1.5 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-500 hover:bg-orange-500/20 transition-colors disabled:opacity-60">
              <Icon name="zap" /> {bulkBusy ? t('att_bulk_busy') : t('att_bulk_button').replace('{n}', overdueCount)}
            </button>
          )}
        </div>
        {/* Row 2: stats — the day's status, shown to the agent too (owner 2026-09-03: "show
            that days attandance status like manager sees"). Counted over the roster DayView was
            given, which for an agent is the ACTIVE one — MyAttendance strips the archive. */}
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <span className="flex items-center gap-1">
            <span><Icon name="calendar" /></span><span className="font-semibold">{totalScheduled}</span>
            <span className="text-muted-foreground">{t('att_working')}</span>
          </span>
          <span className="text-border/40">·</span>
          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
            <span><Icon name="check" /></span><span className="font-semibold">{confirmedCount}</span>
            <span>{t('att_confirmed_count')}</span>
          </span>
          <span className="text-border/40">·</span>
          <span className="flex items-center gap-1 text-amber-500">
            <span><Icon name="hourglass" /></span><span className="font-semibold">{notEnteredCount}</span>
            <span>{t('att_not_entered')}</span>
          </span>
          {overdueCount > 0 && (
            <>
              <span className="text-border/40">·</span>
              <span className="flex items-center gap-1 text-orange-500 font-semibold">
                <span><Icon name="alert-triangle" /></span><span>{overdueCount}</span>
                <span>{t('att_overdue_count')}</span>
              </span>
            </>
          )}
          <span className="text-border/40">·</span>
          <span className="flex items-center gap-1 text-red-500">
            <span><Icon name="x" /></span><span className="font-semibold">{absentCount}</span>
            <span>{t('att_status_absent')}</span>
          </span>
        </div>
        {/* Row 3: legend */}
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"/>
            <span className="text-muted-foreground">{t('att_legend_office')}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0"/>
            <span className="text-muted-foreground">{t('att_legend_remote')}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0"/>
            <span className="text-muted-foreground">{t('att_legend_not_entered')}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500 shrink-0"/>
            <span className="text-muted-foreground">{t('att_legend_absent')}</span>
          </span>
        </div>
      </div>

      {/* Agent grid — working agents only in cards, day-off collapsed to strip */}
      {(() => {
        const sorted = [...agents].sort((a, b) => {
          const aOff = scheduledMinutes(a, date, scheduleHistory, overrideMap) === 0
          const bOff = scheduledMinutes(b, date, scheduleHistory) === 0
          if (aOff !== bOff) return aOff ? 1 : -1
          return a.name.localeCompare(b.name)
        })
        // First-day hires stay visible all day even if today is off in their schedule.
        // The card note adapts: prompt to fill setup only when no weekly schedule exists yet.
        const hasWeeklySchedule = a => DAYS.some(d => daySlots(a, d).length > 0)
        const isOnboarding = a => a.hire_date === date && scheduledMinutes(a, date, scheduleHistory, overrideMap) === 0
        const working    = sorted.filter(a => scheduledMinutes(a, date, scheduleHistory, overrideMap) > 0)
        const onboarding = sorted.filter(isOnboarding)
        const dayOff     = sorted.filter(a => scheduledMinutes(a, date, scheduleHistory, overrideMap) === 0 && !isOnboarding(a))
        return (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
              {expectedCandidates.map(c => (
                <div key={c.id} className="rounded-xl border-2 border-dashed border-cyan-400/60 bg-cyan-50/40 dark:bg-cyan-900/10 p-2.5 text-left">
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-cyan-600 dark:text-cyan-400 font-medium"><Icon name="rocket" size={12} /> {t('att_expected_badge')}{c.start_time ? ` · ${c.start_time.slice(0,5)}` : ''}</div>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-1.5">
                    <button onClick={() => onCandidateShowedUp?.(c)}
                      className="flex-1 text-xs font-semibold py-1 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/30 transition-colors border border-emerald-500/30">
                      {t('att_showed_up')}
                    </button>
                    <button onClick={() => onCandidateNoShow?.(c)}
                      className="flex-1 text-xs font-semibold py-1 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors border border-red-500/25">
                      {t('att_no_show_btn')}
                    </button>
                  </div>
                </div>
              ))}
              {onboarding.map(a => (
                <div key={a.id} className="rounded-xl border-2 border-dashed border-violet-400/60 bg-violet-50/40 dark:bg-violet-900/10 p-2.5 text-left">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate">{a.name}</div>
                    <div className="text-xs text-violet-600 dark:text-violet-400 font-medium">🆕 {t('att_onboarding_badge')}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1.5 leading-snug">{hasWeeklySchedule(a) ? t('att_onboarding_note_ready') : t('att_onboarding_note')}</div>
                </div>
              ))}
              {working.map(agent => <AgentBox ctx={agentBoxCtx} key={agent.id} agent={agent} />)}
              {/* ── THE DAY OFF IS A CARD NOW (owner 2026-09-02) ─────────────────────────
                  It was a row of name pills under the grid. They were clickable — that is
                  how an unscheduled visit gets recorded — but they did not LOOK it, and the
                  owner read the row as a list with "no ability to open modal". A quiet card
                  in the same grid says both things at once: still a day off (dimmed, and it
                  says so), still a door. One day fits; the week view keeps its own compact
                  shape and is untouched. */}
              {dayOff.map(agent => <AgentBox ctx={agentBoxCtx} key={agent.id} agent={agent} />)}
            </div>
            {/* ── A DAY OFF IS NOT A DAY NOBODY CAME IN (owner, need-care list) ──────────
                "someone may come for hours to do something and leave, so we need the option
                to select came at / left at on those who are in Day off" — and to do it HERE,
                not through the separate schedule-override flow in another tab.
                These were inert `<span>`s, so the one thing an agent's day off can produce —
                an unscheduled visit — was the one thing this surface could not record. They
                are buttons now, opening the SAME day modal every working card opens; the
                modal grew an unscheduled-visit row for the no-slots case.
                ⚠️ The card stays visually quiet (muted, small) because a day off is still the
                fact: this is a door, not a promotion to the working grid. And it changes NO
                schedule — an override says "they were rostered after all", this says "they
                dropped in", and those are different claims about the same day. */}
          </>
        )
      })()}
    </div>
  )
}

// below Tailwind's `sm` - live, so rotating a phone or resizing a window switches the Month presentation
function useIsPhone() {
  const q = '(max-width: 639px)'
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches)
  useEffect(() => {
    const mq = window.matchMedia(q)
    const on = () => setM(mq.matches)
    mq.addEventListener?.('change', on)
    return () => mq.removeEventListener?.('change', on)
  }, [])
  return m
}

// ─── Main export ──────────────────────────────────────────────────────────────
export function AttendanceTab({ agents, lang, config = null, onAgentCreated }) {
  // ONE definition of "late". `config.late_threshold_minutes` is the Admin setting the Stats tab
  // already uses; reading it here means this card and that table can never disagree about the
  // same arrival. The `?? 15` mirrors AdminTab's own default, so an unset config behaves the way
  // every other consumer of this key behaves — not the way this file would prefer.
  // ⚠️ 5, NOT 15 (owner 2026-09-10: "late is considered more than 5 minutes", restated as
  // "late mean after 5 minutes, now it still calculates 15"). Measured that day: the config
  // key had NO ROW AT ALL, so every consumer fell back to its own literal 15 and nobody had
  // ever set anything. The row now exists and says 5; this fallback matches it so deleting
  // the row cannot silently restore a rule the owner has rejected.
  const lateThreshold = config?.late_threshold_minutes ?? 5
  const isPhone = useIsPhone()
  useClock(60000)   // office-tz "today" stays correct across midnight + after the server sync lands
  const today = todayStr()   // Tbilisi date, server-corrected — not the device's
  const [monthStr,     setMonthStr]     = useState(today.slice(0, 7))
  const [view,         setView]         = useViewPreference('attendance_view', 'day')   // 'day' | 'week' | 'month'
  const [selectedDate, setSelectedDate] = useState(today)
  const [modal,        setModal]        = useState(null)
  const [agentFilterWeek,  setAgentFilterWeek]  = useState('all')
  const [agentFilterMonth, setAgentFilterMonth] = useState('all')
  const agentFilter    = view === 'week' ? agentFilterWeek : view === 'month' ? agentFilterMonth : 'all'

  // ─── Candidates expected to start (status 'starting') ──────────────────────
  const { candidates, update: updateCandidate, promoteToAgent } = useCandidates()
  const expectedCandidates = useMemo(
    () => candidates.filter(c => c.status === 'starting' && c.start_date === selectedDate),
    [candidates, selectedDate]
  )

  async function handleCandidateShowedUp(c) {
    if (!confirm(t('att_showed_up_confirm').replace('{name}', c.name))) return
    const agent = await promoteToAgent(c.id)
    if (!agent) return
    const nowTs = String(Date.now())
    try {
      localStorage.setItem(`hire_ts_${c.id}`, nowTs)
      localStorage.setItem(`hire_ts_agent_${agent.id}`, JSON.stringify({ ts: nowTs, candidateId: c.id, preHireStatus: 'starting' }))
    } catch {}
    // Auto check-in right now (Tbilisi wall-clock from the server-corrected instant)
    const t2 = tzTimeStr(now())
    await upsert({ agent_id: agent.id, date: selectedDate, status: 'present', check_in: t2, check_out: null, slots: [], confirmed: true, note: null })
    onAgentCreated?.({ ...agent, _preHireStatus: 'starting' })
  }

  async function handleCandidateNoShow(c) {
    if (!confirm(t('att_no_show_confirm').replace('{name}', c.name))) return
    await updateCandidate(c.id, { status: 'interviewed', start_date: null, start_time: null })
    logCandidateEvent(c.id, 'note', t('att_no_show_log'))
  }
  const setAgentFilter = view === 'week' ? setAgentFilterWeek : setAgentFilterMonth

  const { byAgent, scheduleHistory, loading, error, reload, upsert, remove } = useAttendance(monthStr, agents)

  // Dates to show for week/month views
  const { from: mFrom, to: mTo } = monthBounds(monthStr + '-01')
  const allDates = useMemo(() => {
    if (view === 'month') return dateRange(mFrom, mTo)
    if (view === 'week') {
      const ws = weekStart(selectedDate)
      const we = toDateStr(new Date(new Date(ws + 'T12:00:00').getTime() + 6 * 86400000))
      return dateRange(ws, we)
    }
    return [selectedDate]
  }, [view, monthStr, selectedDate])
  // One-off schedule overrides for EVERY date on screen (attendance audit 2026-10-21, A08): a week running into the
  // next month used to load only this month's overrides, so its other days silently fell back to the base schedule.
  const ovFrom = allDates[0] < mFrom ? allDates[0] : mFrom
  const ovTo = allDates[allDates.length - 1] > mTo ? allDates[allDates.length - 1] : mTo
  const { byAgent: overrideMap, error: overridesError } = useOverrides(agents, ovFrom, ovTo)

  const activeAgents = useMemo(() =>
    agents.filter(a => a.is_active !== false).sort((a, b) => a.name.localeCompare(b.name)), [agents])
  const visibleAgents = useMemo(() =>
    activeAgents.filter(a => agentFilter === 'all' || a.id === agentFilter), [activeAgents, agentFilter])

  // ⚠️ ONE period anchor (attendance audit 2026-10-21): the month arrows moved only `monthStr`, so Month → ‹ → Week showed
  // a September heading over October's week (the week reads `selectedDate`). The selected date moves with the month:
  // today in today's month, otherwise the 1st.
  function goMonth(ms) {
    setMonthStr(ms)
    setSelectedDate(today.slice(0, 7) === ms ? today : ms + '-01')
  }
  // Navigation — for day view moves one day, for week/month moves by period
  function prev() {
    if (view === 'day') {
      const d = new Date(selectedDate + 'T12:00:00')
      d.setDate(d.getDate() - 1)
      const s = toDateStr(d)
      setSelectedDate(s)
      setMonthStr(s.slice(0, 7))
    } else if (view === 'week') {
      const d = new Date(selectedDate + 'T12:00:00')
      d.setDate(d.getDate() - 7)
      const s = toDateStr(d)
      setMonthStr(s.slice(0, 7))
      setSelectedDate(s)
    } else {
      const [y, m] = monthStr.split('-').map(Number)
      const d = new Date(y, m - 2, 1)
      goMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`)
    }
  }
  function next() {
    if (view === 'day') {
      const d = new Date(selectedDate + 'T12:00:00')
      d.setDate(d.getDate() + 1)
      const s = toDateStr(d)
      setSelectedDate(s)
      setMonthStr(s.slice(0, 7))
    } else if (view === 'week') {
      const d = new Date(selectedDate + 'T12:00:00')
      d.setDate(d.getDate() + 7)
      const s = toDateStr(d)
      setMonthStr(s.slice(0, 7))
      setSelectedDate(s)
    } else {
      const [y, m] = monthStr.split('-').map(Number)
      const d = new Date(y, m, 1)
      goMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`)
    }
  }

  const periodLabel = view === 'day'
    ? (() => { const d = new Date(selectedDate + 'T12:00:00'); return `${t('days')[(d.getDay()+6)%7]}, ${t('months')[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` })()
    : view === 'week'
      // the week's own dates, not the month it started from (audit 2026-10-21)
      ? (() => {
          const a = new Date(allDates[0] + 'T12:00:00'), b = new Date(allDates[allDates.length - 1] + 'T12:00:00')
          return a.getMonth() === b.getMonth()
            ? `${a.getDate()}–${b.getDate()} ${t('months')[b.getMonth()]} ${b.getFullYear()}`
            : `${a.getDate()} ${t('months')[a.getMonth()]} – ${b.getDate()} ${t('months')[b.getMonth()]} ${b.getFullYear()}`
        })()
      : (() => { const d = new Date(monthStr + '-15'); return `${t('months')[d.getMonth()]} ${d.getFullYear()}` })()

  if (loading) return (
    <div className="flex items-center justify-center py-24 text-muted-foreground text-sm gap-2">
      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
      </svg>
      Loading attendance…
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        {/* Nav + View toggle on same row on mobile */}
        {/* flex-wrap + no fixed label width: at 320 px the row was 388 px wide and pushed the whole page sideways
            (audit 2026-10-21) */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-muted/40 rounded-xl p-1 min-w-0">
            <button type="button" onClick={prev} aria-label={t('att_prev_period')} title={t('att_prev_period')}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-muted-foreground hover:text-foreground">‹</button>
            <span className="px-1 text-sm font-semibold text-center min-w-0" aria-live="polite">{periodLabel}</span>
            <button type="button" onClick={next} aria-label={t('att_next_period')} title={t('att_next_period')}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-muted-foreground hover:text-foreground">›</button>
          </div>

          {/* 🔴 THE "დღეს" BUTTON IS GONE (owner 2026-09-10: "we don't need extra day in weekly
              and month view, we already have day"). It sat immediately left of the
              day / week / month toggle and did two things at once — jump to today AND switch
              the view to `day` — so from the week view it read as a fourth view button that
              silently moved you off the period you were reading. `დღე` in the toggle beside it
              already switches the view, and the ‹ › pair already moves the period, so every
              part of what it did has a control of its own. */}

          {/* View toggle */}
          <div className="flex gap-1 bg-muted/40 rounded-xl p-1" role="group" aria-label={t('att_view_label')}>
            {[['day',t('att_day_view')],['week',t('week')],['month',t('month')]].map(([v, l]) => (
              <button key={v} type="button" aria-pressed={v === view} onClick={() => {
                setView(v)
                if (v === 'day') {
                  const snap = today.slice(0, 7) === monthStr ? today : monthStr + '-01'
                  setSelectedDate(snap)
                }
              }}
                className={`px-2.5 py-1.5 min-h-[36px] rounded-lg text-xs font-medium transition-all capitalize ${v === view ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Agent filter — hidden in day view (shows all) */}
        {view !== 'day' && (
          <AgentSingleSelect
            value={agentFilter}
            onChange={setAgentFilter}
            /* the WHOLE roster (audit 2026-10-21): it was handed the already-filtered list, so after choosing one
               person the only other choice was "All" */
            agents={activeAgents}
            allLabel={t('all_agents')}
          />
        )}


      </div>

      {/* ⚠️ A FAILED READ IS SAID, not drawn as attendance (audit 2026-10-21, A03): the hook's error was never rendered,
          so a cold failure fed empty records into the cards (every agent "no check-in") and a failed refresh kept old
          rows under the new date. */}
      {(error || overridesError) && (
        <div role="status" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-500/40 bg-red-500/5 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          <span>{error ? t('att_read_failed') : t('att_overrides_failed')}</span>
          {error && <button type="button" onClick={() => reload?.()} className="underline">{t('att_retry')}</button>}
        </div>
      )}

      {/* ── Day view ── */}
      {view === 'day' && (
        <DayView
          agents={visibleAgents.length ? visibleAgents : agents.filter(a => a.is_active !== false).sort((a,b) => a.name.localeCompare(b.name))}
          date={selectedDate}
          attendanceByDate={byAgent}
          lang={lang}
          scheduleHistory={scheduleHistory}
          overrideMap={overrideMap}
          onOpenModal={({ agent, date, record }) => setModal({ agent, date, record })}
          onSave={upsert}
          expectedCandidates={expectedCandidates}
          onCandidateShowedUp={handleCandidateShowedUp}
          onCandidateNoShow={handleCandidateNoShow}
          lateThreshold={lateThreshold}
        />
      )}

      {/* ── Week / Month view ── */}
      {view !== 'day' && (
        <div className="space-y-6">
          {view === 'month' && isPhone && visibleAgents.map(agent => (
            <MonthCompactCard key={agent.id} agent={agent} dates={allDates} agentAtt={byAgent[agent.id] || {}}
              scheduleHistory={scheduleHistory} overrideMap={overrideMap} lateThreshold={lateThreshold} today={today}
              onOpenDay={date => setModal({ agent, date, record: (byAgent[agent.id] || {})[date] })} />
          ))}
          {!(view === 'month' && isPhone) && visibleAgents.map(agent => {
            const agentAtt = byAgent[agent.id] || {}
            return (
              <div key={agent.id} className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-sm">{agent.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{agent.role}</span>
                  </div>
                </div>
                <div className="px-4 pt-3 pb-2">
                  <AgentSummaryRow agent={agent} dates={allDates} attendanceByDate={agentAtt} scheduleHistory={scheduleHistory} overrideMap={overrideMap} lateThreshold={lateThreshold} today={today} />
                </div>
                <div className="px-4 pb-4 overflow-x-auto">
                  <div className="grid gap-1 mt-2 mb-1"
                    style={{ gridTemplateColumns: `repeat(${allDates.length}, minmax(52px, 1fr))` }}>
                    {allDates.map(date => {
                      const d = new Date(date + 'T12:00:00')
                      const isToday = date === today
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6
                      return (
                        <div key={date} className={`text-center text-xs font-medium leading-none py-1 ${isToday ? 'text-primary font-bold' : isWeekend ? 'text-muted-foreground/50' : 'text-muted-foreground'}`}>
                          <div>{t('days')[(d.getDay() + 6) % 7]}</div>
                          <div className={`mt-0.5 ${isToday ? 'bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center mx-auto text-xs' : ''}`}>
                            {d.getDate()}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="grid gap-1"
                    style={{ gridTemplateColumns: `repeat(${allDates.length}, minmax(52px, 1fr))` }}>
                    {allDates.map(date => (
                      <DayCell key={date} agent={agent} date={date} record={agentAtt[date]}
                        scheduleHistory={scheduleHistory} overrideMap={overrideMap}
                        onClick={() => setModal({ agent, date, record: agentAtt[date] })} />
                    ))}
                  </div>
                </div>
              </div>
            )
          })}

          {/* Team summary */}
          {agentFilter === 'all' && visibleAgents.length > 1 && (
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="text-sm font-semibold mb-3">{t('att_team_summary')}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50">
                      {[t('att_col_agent'),t('att_col_scheduled'),t('att_col_worked'),t('att_col_delta'),t('att_summary_late'),t('att_summary_overtime'),t('att_col_absent'),t('att_col_confirmed'),t('att_col_assumed'),t('att_col_remote')].map(h => (
                        <th key={h} className="text-left text-xs font-medium text-muted-foreground py-2 pr-4 first:pl-0">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleAgents.map(agent => {
                      const sum = summarise(agent, allDates, byAgent[agent.id] || {}, scheduleHistory, overrideMap, lateThreshold, today)
                      const dc = sum.deltaMins < -30 ? 'text-red-500' : sum.deltaMins > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                      return (
                        <tr key={agent.id} className="border-b border-border/50 last:border-0">
                          <td className="py-2 pr-4 font-medium whitespace-nowrap">{agent.name}</td>
                          <td className="py-2 pr-4 tabular-nums">{fmtDuration(sum.scheduledMins)}</td>
                          <td className="py-2 pr-4 tabular-nums">{fmtDuration(sum.workedMins)}</td>
                          <td className={`py-2 pr-4 tabular-nums font-semibold ${dc}`}>{fmtDelta(sum.deltaMins)}</td>
                          {/* Same two figures as the per-agent cards above, so the roster table
                              and the card cannot report different weeks for one person. */}
                          <td className={`py-2 pr-4 tabular-nums ${sum.lateDays ? 'text-amber-500' : ''}`}>
                            {sum.lateDays ? <>{sum.lateDays}d <span className="text-xs opacity-70">{fmtDuration(sum.lateMins)}</span></> : '0d'}
                          </td>
                          <td className={`py-2 pr-4 tabular-nums ${sum.overtimeMins ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                            {sum.overtimeMins ? fmtDuration(sum.overtimeMins) : '0'}
                          </td>
                          <td className="py-2 pr-4">{sum.absentDays}d</td>
                          <td className="py-2 pr-4 text-emerald-600 dark:text-emerald-400">{sum.confirmedDays}d</td>
                          <td className="py-2 pr-4 text-amber-500">{sum.assumedDays}d</td>
                          <td className="py-2 pr-4">{sum.remoteDays}d</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Entry modal */}
      {modal && (
        <EntryModal
          agent={modal.agent}
          date={modal.date}
          record={modal.record}
          onSave={upsert}
          onDelete={remove}
          onClose={() => setModal(null)}
          lang={lang}
          scheduleHistory={scheduleHistory}
          overrideMap={overrideMap}
        />
      )}
    </div>
  )
}
